/* ==========================================================================
   Final Announcement Bar — continuous scrolling marquee
   All JS for sections/announcement-bar.liquid lives here.
   Every query is scoped under .final-announcement-bar.
   Responsibilities:
     - Duplicate the marquee group enough times to fill the viewport so the
       loop has no visible gap / jump.
     - Compute the exact pixel shift + duration from the "Scroll speed" setting.
     - Honor prefers-reduced-motion (no auto-scroll).
   Pause-on-hover is handled in CSS (:hover) so it needs no JS here.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT_SELECTOR = '.final-announcement-bar';
  var TRACK_SELECTOR = '.final-announcement-bar__track';
  var GROUP_SELECTOR = '.final-announcement-bar__group';
  var VIEWPORT_SELECTOR = '.final-announcement-bar__viewport';

  function prefersReducedMotion() {
    return window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function build(bar) {
    var track = bar.querySelector(TRACK_SELECTOR);
    if (!track) return;

    var firstGroup = track.querySelector(GROUP_SELECTOR);
    if (!firstGroup) return;

    // Reduced motion: leave it static, never scroll.
    if (prefersReducedMotion()) {
      track.classList.remove('is-running');
      return;
    }

    // Remove any clones from a previous build (e.g. after a resize).
    track.querySelectorAll(GROUP_SELECTOR + '[data-clone="true"]').forEach(function (clone) {
      clone.remove();
    });
    track.classList.remove('is-running');

    var groupWidth = firstGroup.getBoundingClientRect().width;
    if (!groupWidth) return; // not laid out yet

    var viewport = bar.querySelector(VIEWPORT_SELECTOR) || bar;
    var viewportWidth = viewport.getBoundingClientRect().width;

    // Duplicate the group until the track is comfortably wider than ~2×
    // the viewport, with a minimum of 2 groups so the seam is always covered.
    var copiesNeeded = Math.max(2, Math.ceil((viewportWidth * 2) / groupWidth) + 1);
    for (var i = 1; i < copiesNeeded; i++) {
      var clone = firstGroup.cloneNode(true);
      clone.setAttribute('data-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    }

    // Scroll speed in px/second (from the section setting), with a safe default.
    var speed = parseFloat(bar.getAttribute('data-speed'));
    if (!speed || speed <= 0) speed = 60;

    // Shift by exactly one group width so copy #2 lands where copy #1 began.
    track.style.setProperty('--marquee-shift', groupWidth + 'px');
    track.style.setProperty('--marquee-duration', (groupWidth / speed) + 's');
    track.classList.add('is-running');
  }

  function initBar(bar) {
    if (!bar) return;
    build(bar);

    // Rebuild on resize so the duplication still fills the viewport.
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      var track = bar.querySelector(TRACK_SELECTOR);
      if (track) track.classList.remove('is-running');
      resizeTimer = setTimeout(function () { build(bar); }, 200);
    });
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor: re-init when the section is reloaded.
  document.addEventListener('shopify:section:load', function (event) {
    var bar = event.target.querySelector(ROOT_SELECTOR);
    if (bar) initBar(bar);
  });
})();

/* ==========================================================================
   Final Menu Drawer — close button
   The redesigned drawer has its own close (×) button in the top bar. Rather
   than reimplement Dawn's open/close animation, we just forward the click to
   the native <summary> toggle, which runs Dawn's HeaderDrawer.closeMenuDrawer
   (animation, focus-trap release, and body scroll lock all handled for us).
   Delegated so it keeps working after Shopify theme-editor section reloads.
   ========================================================================== */
(function () {
  'use strict';

  var DRAWER_SELECTOR = '.final-drawer .menu-drawer-container';

  function drawerContainer() {
    return document.querySelector(DRAWER_SELECTOR);
  }

  // OPEN: the trigger lives in the header, outside the drawer. Forward its click
  // to the drawer's hidden <summary> so Dawn's MenuDrawer runs the open animation,
  // focus trap and body scroll-lock.
  document.addEventListener('click', function (event) {
    if (!event.target.closest('[data-final-drawer-open]')) return;
    var container = drawerContainer();
    if (!container || container.hasAttribute('open')) return;
    var summary = container.querySelector('summary');
    if (summary) summary.click();
  });

  // CLOSE: the × button (and the full-screen backdrop) forward to the same
  // <summary>, which Dawn closes properly (animation + scroll-lock release).
  document.addEventListener('click', function (event) {
    var closeBtn = event.target.closest('[data-final-drawer-close]');
    if (!closeBtn) return;
    var container = closeBtn.closest('.menu-drawer-container');
    if (!container || !container.hasAttribute('open')) return;
    var summary = container.querySelector('summary');
    if (summary) summary.click();
  });

  // The × is a role="button" span (kept a non-<button> so Dawn's MenuDrawer
  // doesn't treat it as a submenu back-button). Wire keyboard activation.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    var closeBtn = event.target.closest('[data-final-drawer-close]');
    if (!closeBtn) return;
    event.preventDefault();
    closeBtn.click();
  });
})();

/* ==========================================================================
   Product card image slider — arrows scroll the scroll-snap track by one slide.
   ========================================================================== */
(function () {
  'use strict';
  // Capture phase + stopImmediatePropagation so the click never reaches the
  // card's link/navigation handlers (clicking an arrow must NOT open the product).
  document.addEventListener(
    'click',
    function (event) {
      var arrow = event.target.closest('.card-media-slider__arrow');
      if (!arrow) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      var slider = arrow.closest('.card-media-slider');
      var track = slider && slider.querySelector('.card-media-slider__track');
      if (!track) return;
      var dir = arrow.classList.contains('card-media-slider__arrow--next') ? 1 : -1;
      track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
    },
    true
  );
})();

/* ==========================================================================
   Product card slider state — disable prev/next at the ends, sync dots,
   and let dot clicks jump to a slide.
   ========================================================================== */
(function () {
  'use strict';

  function init(root) {
    (root || document).querySelectorAll('.card-media-slider').forEach(function (slider) {
      if (slider._sliderInit) return;
      slider._sliderInit = true;

      var track = slider.querySelector('.card-media-slider__track');
      if (!track) return;
      var slides = slider.querySelectorAll('.card-media-slider__slide');
      var dots = slider.querySelectorAll('.card-media-slider__dot');
      var prev = slider.querySelector('.card-media-slider__arrow--prev');
      var next = slider.querySelector('.card-media-slider__arrow--next');
      if (!slides.length) return;

      function setActive(i) {
        for (var d = 0; d < dots.length; d++) {
          dots[d].classList.toggle('is-active', d === i);
        }
        if (prev) prev.classList.toggle('is-disabled', i <= 0);
        if (next) next.classList.toggle('is-disabled', i >= slides.length - 1);
      }

      // Track the most-visible slide with an IntersectionObserver rooted on the
      // scroll track — reliable for touch swipes (scroll-snap) and arrow scrolls.
      if ('IntersectionObserver' in window) {
        var ratios = new Array(slides.length);
        for (var r = 0; r < ratios.length; r++) ratios[r] = 0;
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (e) {
              var i = Array.prototype.indexOf.call(slides, e.target);
              if (i > -1) ratios[i] = e.intersectionRatio;
            });
            var maxI = 0;
            var maxR = -1;
            for (var k = 0; k < ratios.length; k++) {
              if (ratios[k] > maxR) { maxR = ratios[k]; maxI = k; }
            }
            setActive(maxI);
          },
          { root: track, threshold: [0, 0.25, 0.5, 0.75, 1] }
        );
        for (var s = 0; s < slides.length; s++) io.observe(slides[s]);
      } else {
        // Fallback for very old browsers: derive index from scroll position.
        var ticking = false;
        track.addEventListener('scroll', function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(function () {
            setActive(track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0);
            ticking = false;
          });
        }, { passive: true });
      }

      setActive(0);
    });
  }

  // Dot click → jump to that slide (capture + stop so it never navigates).
  document.addEventListener('click', function (event) {
    var dot = event.target.closest('.card-media-slider__dot');
    if (!dot) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    var slider = dot.closest('.card-media-slider');
    var track = slider && slider.querySelector('.card-media-slider__track');
    if (!track) return;
    var idx = parseInt(dot.getAttribute('data-index'), 10) || 0;
    track.scrollTo({ left: idx * track.clientWidth, behavior: 'smooth' });
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  // Re-init sliders for cards appended by infinite scroll.
  document.addEventListener('miyo:cards-added', function (e) {
    init((e.detail && e.detail.container) || document);
  });

  // Catch cards injected by other means (e.g. product recommendations fetched
  // by Dawn's <product-recommendations>). Debounced; init() skips wired sliders.
  if ('MutationObserver' in window) {
    var moPending = false;
    new MutationObserver(function () {
      if (moPending) return;
      moPending = true;
      window.requestAnimationFrame(function () {
        moPending = false;
        init(document);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();

/* ==========================================================================
   Collection infinite scroll
   Replaces numbered pagination: when the [data-infinite-scroll] sentinel nears
   the viewport, fetch the next page, append its products, and update the
   sentinel's next-page URL (removing it on the last page). The sentinel is
   re-queried each time, so it keeps working after facet/sort updates replace
   the grid. A spinner in the sentinel shows the loading state.
   ========================================================================== */
(function () {
  'use strict';

  var loading = false;

  function getTrigger() {
    return document.querySelector('[data-infinite-scroll]');
  }

  function loadMore() {
    if (loading) return;
    var trigger = getTrigger();
    if (!trigger) return;
    var url = trigger.getAttribute('data-next-url');
    if (!url) return;

    loading = true;
    trigger.classList.add('is-loading');

    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var newGrid = doc.getElementById('product-grid');
        var grid = document.getElementById('product-grid');

        if (newGrid && grid) {
          var frag = document.createDocumentFragment();
          newGrid.querySelectorAll('.grid__item').forEach(function (li) {
            // strip reveal-on-scroll classes so appended items are not stuck hidden
            li.classList.remove('scroll-trigger', 'animate--slide-in');
            frag.appendChild(li);
          });
          grid.appendChild(frag);
          document.dispatchEvent(new CustomEvent('miyo:cards-added', { detail: { container: grid } }));
        }

        // Advance (or end) pagination using the fetched page's sentinel.
        var newTrigger = doc.querySelector('[data-infinite-scroll]');
        var nextUrl = newTrigger && newTrigger.getAttribute('data-next-url');
        if (nextUrl) {
          trigger.setAttribute('data-next-url', nextUrl);
        } else if (trigger.parentNode) {
          trigger.parentNode.removeChild(trigger);
        }
      })
      .catch(function () { /* leave the sentinel so the next scroll retries */ })
      .finally(function () {
        loading = false;
        var t = getTrigger();
        if (t) {
          t.classList.remove('is-loading');
          // If the sentinel is still in view (tall viewport), keep loading.
          window.requestAnimationFrame(check);
        }
      });
  }

  function check() {
    var trigger = getTrigger();
    if (!trigger || loading) return;
    var rect = trigger.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top <= vh + 600) loadMore(); // 600px prefetch margin
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () { check(); ticking = false; });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  document.addEventListener('shopify:section:load', check);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', check);
  } else {
    check();
  }
})();

/* ==========================================================================
   Product card Quick View
   Self-contained popup built from the Shopify product JSON (/products/handle.js)
   so it works regardless of the product page template. Handles variant options,
   price, and add-to-cart that reuses Dawn's cart drawer / notification.
   Triggered by [data-quickview][data-product-url].
   ========================================================================== */
(function () {
  'use strict';

  var overlay = null;
  var current = { product: null, variant: null, url: '' };

  function currency() {
    return (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  }
  function money(cents) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency() }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function ensureModal() {
    if (overlay) return;
    overlay = document.createElement('div');
    overlay.className = 'miyo-qv-overlay';
    overlay.hidden = true;
    overlay.innerHTML =
      '<div class="miyo-qv" role="dialog" aria-modal="true" aria-label="Quick view">' +
        '<button class="miyo-qv__close" type="button" aria-label="Close" data-qv-close>&times;</button>' +
        '<div class="miyo-qv__body"></div>' +
      '</div>';
    document.body.appendChild(overlay);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay || e.target.closest('[data-qv-close]')) closeQV();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay && !overlay.hidden) closeQV();
    });
  }

  function closeQV() {
    if (!overlay) return;
    overlay.hidden = true;
    document.body.classList.remove('miyo-qv-open');
  }

  function openQV(url) {
    ensureModal();
    current.url = url;
    var body = overlay.querySelector('.miyo-qv__body');
    body.innerHTML = '<div class="miyo-qv__loading">Loading…</div>';
    overlay.hidden = false;
    document.body.classList.add('miyo-qv-open');

    // Build the product-JSON URL: drop any query/hash and trailing slash, then add .js
    var jsonUrl = url.split('#')[0].split('?')[0].replace(/\/+$/, '') + '.js';
    fetch(jsonUrl, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status + ' for ' + jsonUrl);
        return r.json();
      })
      .then(function (p) { current.product = p; render(p); })
      .catch(function (err) {
        if (window.console && console.error) console.error('Quick view failed:', err);
        body.innerHTML = '<div class="miyo-qv__loading">Could not load this product. ' +
          '<a href="' + url + '">View product</a></div>';
      });
  }

  function optionNames(p) {
    return (p.options || []).map(function (o) { return typeof o === 'string' ? o : o.name; });
  }
  function isDefaultOnly(p) {
    var names = optionNames(p);
    return p.variants.length <= 1 || (names.length === 1 && names[0] === 'Title');
  }
  function findVariant(p, selected) {
    return p.variants.filter(function (v) {
      for (var i = 0; i < selected.length; i++) {
        if ((v.options[i] || '') !== selected[i]) return false;
      }
      return true;
    })[0];
  }
  function firstAvailable(p) {
    return p.variants.filter(function (v) { return v.available; })[0] || p.variants[0];
  }

  function render(p) {
    var body = overlay.querySelector('.miyo-qv__body');
    current.variant = firstAvailable(p);

    var img = (current.variant && current.variant.featured_image && current.variant.featured_image.src) ||
      p.featured_image || (p.images && p.images[0]) || '';

    var html = '<div class="miyo-qv__grid">';
    html += '<div class="miyo-qv__media">' + (img ? '<img alt="" src="' + img + '">' : '') + '</div>';
    html += '<div class="miyo-qv__info">';
    html += '<h2 class="miyo-qv__title">' + p.title + '</h2>';
    html += '<div class="miyo-qv__price" data-qv-price></div>';

    if (!isDefaultOnly(p)) {
      var names = optionNames(p);
      names.forEach(function (name, idx) {
        var values = [];
        p.variants.forEach(function (v) {
          if (values.indexOf(v.options[idx]) === -1) values.push(v.options[idx]);
        });
        html += '<div class="miyo-qv__option"><label>' + name + '</label><select data-qv-option="' + idx + '">';
        values.forEach(function (val) {
          var sel = current.variant && current.variant.options[idx] === val ? ' selected' : '';
          html += '<option value="' + val.replace(/"/g, '&quot;') + '"' + sel + '>' + val + '</option>';
        });
        html += '</select></div>';
      });
    }

    html += '<button type="button" class="miyo-qv__add" data-qv-add></button>';
    html += '<a class="miyo-qv__view" href="' + current.url + '">View full details</a>';
    html += '</div></div>';
    body.innerHTML = html;

    body.querySelectorAll('[data-qv-option]').forEach(function (sel) {
      sel.addEventListener('change', onOptionChange);
    });
    body.querySelector('[data-qv-add]').addEventListener('click', addToCart);
    updateVariantUI();
  }

  function onOptionChange() {
    var p = current.product;
    var selects = overlay.querySelectorAll('[data-qv-option]');
    var selected = Array.prototype.map.call(selects, function (s) { return s.value; });
    current.variant = findVariant(p, selected);
    updateVariantUI();
  }

  function updateVariantUI() {
    var p = current.product;
    var v = current.variant;
    var priceEl = overlay.querySelector('[data-qv-price]');
    var addBtn = overlay.querySelector('[data-qv-add]');
    var media = overlay.querySelector('.miyo-qv__media img');

    if (v && v.featured_image && v.featured_image.src && media) media.src = v.featured_image.src;

    if (!v) {
      priceEl.innerHTML = '';
      addBtn.textContent = 'Unavailable';
      addBtn.disabled = true;
      return;
    }

    var priceHTML = '<span class="now">' + money(v.price) + '</span>';
    if (v.compare_at_price && v.compare_at_price > v.price) {
      var off = Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price);
      priceHTML += '<span class="was">' + money(v.compare_at_price) + '</span>';
      priceHTML += '<span class="save">-' + off + '%</span>';
    }
    priceEl.innerHTML = priceHTML;

    addBtn.disabled = !v.available;
    addBtn.textContent = v.available ? 'Add to cart' : 'Sold out';
  }

  function addToCart() {
    var v = current.variant;
    if (!v || !v.available) return;

    var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    var payload = { id: v.id, quantity: 1 };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      payload.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
      payload.sections_url = window.location.pathname;
    }

    var addBtn = overlay.querySelector('[data-qv-add]');
    addBtn.disabled = true;
    addBtn.textContent = 'Adding…';

    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res.status) { addBtn.textContent = 'Add to cart'; addBtn.disabled = false; return; }
        closeQV();
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(res);
          if (typeof cart.open === 'function') cart.open();
        } else {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        }
      })
      .catch(function () { addBtn.textContent = 'Add to cart'; addBtn.disabled = false; });
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-quickview]');
    if (!trigger) return;
    event.preventDefault();
    var url = trigger.getAttribute('data-product-url');
    if (url) openQV(url);
  });
})();
